/**
 * WeaveIn.ai Platform - Workflow Step Builder Component
 * Visual workflow designer for creating and connecting workflow steps
 * Enhanced with StepTypePicker and StepConfigurationPanel for better UX
 */

import React, { useState, useEffect } from 'react';
import { useWorkflowSteps } from '@/hooks/useWorkflows';
import { WorkflowStep, WorkflowStepCreate, StepType } from '@/services/api/workflows';
import { config } from '@/src/config';
import { Edit, Save, X, Brain, ClipboardList, ArrowLeft } from 'lucide-react';
import { StepTypePicker, getStepTypeOption, getStepTypeFromConfig, StepTypeOption } from './StepTypePicker';
import { StepConfigurationPanel } from './StepConfigurationPanel';
import { StepPlacementFields } from './StepPlacementFields';
import { WorkflowNotesPanel } from './WorkflowNotesPanel';
import { WorkflowCanvas } from './WorkflowCanvas';
import { CanvasShape } from './canvasGeometry';
import WorkflowAISuggestions from './WorkflowAISuggestions';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { firstMessageStepVariableProblem } from './messageStepVariables';

type ViewMode = 'classic' | 'enhanced';

interface WorkflowStepBuilderProps {
  templateId: number;
  templateName: string;
  onClose?: () => void;
}

export const WorkflowStepBuilder: React.FC<WorkflowStepBuilderProps> = ({
  templateId,
  templateName,
  onClose,
}) => {
  const { t } = useAppTranslation();
  const { steps, loading, error, createStep, updateStep, deleteStep, refetch } = useWorkflowSteps(templateId);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStep, setEditingStep] = useState<WorkflowStep | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('enhanced');
  const [showAISuggestions, setShowAISuggestions] = useState(true);

  // Steps, or the reasoning behind them. A tab rather than a side panel: these
  // are read at different moments — the background when deciding what the
  // workflow should be, the steps when building it — and showing both at once
  // halves the room for each.
  const [pane, setPane] = useState<'steps' | 'notes'>('steps');
  /** Set when the Background pane was opened FROM a step, so the notes list is
   *  filtered to it and a new note is pinned to it. Cleared when the pane is
   *  opened from the toolbar, which means "the whole workflow". */
  const [backgroundForStep, setBackgroundForStep] = useState<number | null>(null);

  // Fetch configured document types from admin doc_types system
  const [configuredDocTypes, setConfiguredDocTypes] = useState<Array<{id: number; name: string; category_name: string}>>([]);
  const [formTemplates, setFormTemplates] = useState<Array<{id: number; name: string; form_type: string}>>([]);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    const headers = { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId || '' };

    // Fetch doc types
    fetch(`${config.weaveinApi.baseUrl}/api/doc-types?include_inactive=false`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setConfiguredDocTypes(data.map((dt: any) => ({ id: dt.id, name: dt.name, category_name: dt.category_name }))))
      .catch(() => {});

    // Fetch form templates for assessment picker
    fetch(`${config.weaveinApi.baseUrl}/api/forms/templates?is_active=true`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setFormTemplates(Array.isArray(data) ? data.map((f: any) => ({ id: f.id, name: f.name, form_type: f.form_type })) : []))
      .catch(() => {});
  }, []);

  // Handle adding a suggested step
  const handleAddSuggestedStep = (suggestion: {
    step_type: StepType;
    step_name: string;
    step_config: Record<string, any>;
  }) => {
    setFormData({
      step_order: steps.length + 1,
      step_type: suggestion.step_type,
      step_name: suggestion.step_name,
      step_config: suggestion.step_config,
      required: true,
      page_key: null,
      location_hint: null,
      is_user_facing: true,
      step_group: null,
    });
    setSelectedStepTypeValue(suggestion.step_type);
    setShowAddForm(true);
  };

  // Track the selected step type value (e.g., 'communication_sms', 'form', etc.)
  const [selectedStepTypeValue, setSelectedStepTypeValue] = useState('task');

  // Sections already used in this workflow, offered as suggestions. Without
  // them a second step lands in "Kickoff " or "kickoff" and one section
  // quietly becomes two.
  const existingGroups = React.useMemo(
    () => Array.from(new Set(steps.map((s) => s.step_group).filter(Boolean) as string[])).sort(),
    [steps]
  );

  const [formData, setFormData] = useState<Partial<WorkflowStepCreate>>({
    step_order: steps.length + 1,
    step_type: 'task',
    step_name: '',
    step_config: {},
    required: true,
    // Seeded here as well as in resetForm so a step created straight after one
    // that was edited does not inherit the previous step's placement.
    page_key: null,
    location_hint: null,
    is_user_facing: true,
    step_group: null,
  });

  const [configKey, setConfigKey] = useState('');
  const [configValue, setConfigValue] = useState('');

  const resetForm = () => {
    setFormData({
      step_order: steps.length + 1,
      step_type: 'task',
      step_name: '',
      step_config: {},
      required: true,
      page_key: null,
      location_hint: null,
      is_user_facing: true,
      step_group: null,
    });
    setSelectedStepTypeValue('task');
    setConfigKey('');
    setConfigValue('');
    setShowAddForm(false);
    setEditingStep(null);
  };

  // Handle step type selection from the new StepTypePicker
  const handleStepTypeSelect = (value: string, baseType: StepType, defaultConfig: Record<string, any>) => {
    setSelectedStepTypeValue(value);
    setFormData({
      ...formData,
      step_type: baseType,
      step_config: {
        ...defaultConfig,
        ...formData.step_config,
      },
    });
  };

  // Handle configuration changes from StepConfigurationPanel
  const handleConfigChange = (newConfig: Record<string, any>) => {
    setFormData({
      ...formData,
      step_config: newConfig,
    });
  };

  // Get the display label for a step type
  const getStepTypeLabel = (step: WorkflowStep): string => {
    const stepTypeOption = getStepTypeFromConfig(step.step_type, step.step_config);
    return stepTypeOption?.label || step.step_type.replace(/_/g, ' ');
  };

  // Get the icon for a step from the new step type system
  const getStepIcon = (step: WorkflowStep): React.ReactNode => {
    const stepTypeOption = getStepTypeFromConfig(step.step_type, step.step_config);
    return stepTypeOption?.icon || <span style={{ fontSize: '24px' }}>{getStepTypeIcon(step.step_type)}</span>;
  };

  const handleAddConfig = () => {
    if (configKey && configValue) {
      setFormData({
        ...formData,
        step_config: {
          ...formData.step_config,
          [configKey]: configValue,
        },
      });
      setConfigKey('');
      setConfigValue('');
    }
  };

  const handleRemoveConfig = (key: string) => {
    const newConfig = { ...formData.step_config };
    delete newConfig[key];
    setFormData({ ...formData, step_config: newConfig });
  };

  /** A member-message step whose template uses a variable the org does not have. */
  const messageVariableProblem = () =>
    firstMessageStepVariableProblem(
      [{ step_name: formData.step_name, step_type: selectedStepTypeValue || formData.step_type, config: formData.step_config }],
      t,
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const variableProblem = await messageVariableProblem();
    if (variableProblem) {
      alert(variableProblem);
      return;
    }

    const success = await createStep({
      ...formData,
      workflow_template_id: templateId,
    } as WorkflowStepCreate);

    if (success) {
      alert('Step added successfully');
      resetForm();
      refetch();
    }
  };

  const handleEdit = (step: WorkflowStep) => {
    setEditingStep(step);
    setFormData({
      step_order: step.step_order,
      step_type: step.step_type,
      step_name: step.step_name,
      step_config: step.step_config,
      required: step.required,
      // Loaded, not defaulted. Omitting these would make every save from the
      // edit form clear a page_key somebody had already set — and nothing about
      // the request or the response would say so.
      page_key: step.page_key ?? null,
      location_hint: step.location_hint ?? null,
      is_user_facing: step.is_user_facing !== false,
      step_group: step.step_group ?? null,
    });
    // Determine the step type value from the base type and config
    const stepTypeOption = getStepTypeFromConfig(step.step_type, step.step_config);
    setSelectedStepTypeValue(stepTypeOption?.value || step.step_type);
    setShowAddForm(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStep) return;
    const variableProblem = await messageVariableProblem();
    if (variableProblem) {
      alert(variableProblem);
      return;
    }

    const success = await updateStep(editingStep.id, formData);
    if (success) {
      alert('Step updated successfully');
      resetForm();
      refetch();
    }
  };

  const handleDelete = async (step: WorkflowStep) => {
    if (window.confirm(`Are you sure you want to delete step "${step.step_name}"?`)) {
      const success = await deleteStep(step.id);
      if (success) {
        alert('Step deleted successfully');
        refetch();
      }
    }
  };

  const handleReorder = async (reorderedSteps: WorkflowStep[]) => {
    // Update each step with its new order
    for (const step of reorderedSteps) {
      await updateStep(step.id, { step_order: step.step_order });
    }
    refetch();
  };

  const getStepTypeIcon = (type: StepType) => {
    switch (type) {
      case 'task': return '📋';
      case 'notification': return '🔔';
      case 'communication': return '📧';
      case 'wait_for_response': return '⏳';
      case 'approval': return '✅';
      case 'condition': return '🔀';
      case 'api_call': return '🔌';
      case 'wait': return '⏱️';
      case 'form': return '📝';
      case 'billing': return '💰';
      case 'pricing_agent': return '🤖';
      default: return '📄';
    }
  };

  const getStepTypeColor = (type: StepType) => {
    switch (type) {
      case 'task': return 'bg-blue-100 border-blue-300';
      case 'notification': return 'bg-yellow-100 border-yellow-300';
      case 'communication': return 'bg-cyan-100 border-cyan-300';
      case 'wait_for_response': return 'bg-amber-100 border-amber-300';
      case 'approval': return 'bg-green-100 border-green-300';
      case 'condition': return 'bg-purple-100 border-purple-300';
      case 'api_call': return 'bg-orange-100 border-orange-300';
      case 'wait': return 'bg-gray-100 border-gray-300';
      case 'form': return 'bg-indigo-100 border-indigo-300';
      case 'billing': return 'bg-emerald-100 border-emerald-300';
      case 'pricing_agent': return 'bg-violet-100 border-violet-300';
      default: return 'bg-gray-100 border-gray-300';
    }
  };

  return (
    <div className="field-mapping-container" style={{ background: '#ffffff' }}>
      <div className="mapping-header" style={{ flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <h1 style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Workflow Designer — {templateName}</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
          {/* View mode is always 'enhanced' — classic editor removed */}
          {/* AI Suggestions Toggle */}
          <button
            type="button"
            onClick={() => setShowAISuggestions(!showAISuggestions)}
            title={showAISuggestions ? 'Hide AI Suggestions' : 'Show AI Suggestions'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              backgroundColor: showAISuggestions ? '#eef2ff' : '#f9fafb',
              color: showAISuggestions ? '#4f46e5' : '#374151',
              fontWeight: showAISuggestions ? 600 : 400,
              transition: 'all 0.15s ease'
            }}
          >
            <Brain className="w-4 h-4" />
            <span style={{ fontSize: '13px' }}>AI Assist</span>
          </button>
          <button
            type="button"
            className={pane === 'notes' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => {
              setBackgroundForStep(null);
              setPane(pane === 'notes' ? 'steps' : 'notes');
            }}
            title={t(
              'workflows.notes.toggleHelp',
              'Meetings, decisions and constraints behind this workflow'
            )}
          >
            <ClipboardList className="w-4 h-4" style={{ display: 'inline', marginRight: '5px' }} />
            {t('workflows.notes.toggle', 'Background')}
          </button>
          {onClose && (
            <button className="btn btn-secondary" onClick={onClose}>
              <X className="w-4 h-4" style={{ display: 'inline', marginRight: '5px' }} />
              {t('workflows.close', 'Close')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>{t('workflows.error', 'Error:')}</strong> {error}
        </div>
      )}

      {/* The reasoning behind the workflow. Returns early: everything below is
          the step canvas and its modals, none of which belong on this pane. */}
      {pane === 'notes' && (
        <div style={{ padding: '20px' }}>
          <WorkflowNotesPanel
            templateId={templateId}
            steps={steps}
            focusStepId={backgroundForStep}
            onClearFocus={() => setBackgroundForStep(null)}
          />
        </div>
      )}

      {pane === 'steps' && (
        <>
      {/* The step editor is a PAGE, not a modal.
          A step carries a type picker, a configuration panel, placement,
          sections and a document list — far more than fits in a 700px box at
          90vh, so the old modal scrolled its own middle while the page behind
          it stayed put. Worse, every dropdown inside it had to fight the
          modal's stacking and scroll locking, which is exactly how the page
          picker ended up rendering underneath the fields below it.
          Taking the canvas's place removes both problems rather than tuning
          around them. */}
      {showAddForm && (
        <div style={{ padding: '20px' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
            paddingBottom: 12, borderBottom: '1px solid #e5e7eb',
          }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={resetForm}
              title={t('workflows.backToCanvasHelp', 'Discard and return to the workflow')}
            >
              <ArrowLeft className="w-4 h-4" style={{ display: 'inline', marginRight: 5 }} />
              {t('workflows.backToCanvas', 'Back to workflow')}
            </button>
            <h2 style={{ margin: 0, fontSize: 18 }}>
              {editingStep
                ? t('workflows.edit_step_title', 'Edit Step')
                : t('workflows.add_step_title', 'Add New Step')}
            </h2>
            {editingStep && (
              <span style={{ fontSize: 13, color: '#6b7280' }}>
                {t('workflows.step_n', 'Step {{n}}', { n: editingStep.step_order })} · {editingStep.step_name}
              </span>
            )}
          </div>

          <form onSubmit={editingStep ? handleUpdate : handleSubmit} className="mapping-form">
              <div className="form-group">
                <label>{t('workflows.step_order', 'Step Order')}<span className="required">*</span></label>
                <input
                  type="number"
                  min="1"
                  value={formData.step_order}
                  onChange={(e) => setFormData({ ...formData, step_order: parseInt(e.target.value) })}
                  required
                />
                <small style={{ color: '#666' }}>{t('workflows.steps_execute_in_ascending_order', 'Steps execute in ascending order')}</small>
              </div>

              <div className="form-group">
                <label>{t('workflows.step_name', 'Step Name')}<span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.step_name}
                  onChange={(e) => setFormData({ ...formData, step_name: e.target.value })}
                  placeholder="e.g., Send Welcome Email"
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('workflows.step_type', 'Step Type')}<span className="required">*</span></label>
                {viewMode === 'enhanced' ? (
                  <StepTypePicker
                    selectedType={selectedStepTypeValue}
                    onSelect={handleStepTypeSelect}
                    variant="dropdown"
                  />
                ) : (
                  <select
                    value={formData.step_type}
                    onChange={(e) => setFormData({ ...formData, step_type: e.target.value as StepType })}
                    required
                  >
                    <option value="task">{t('workflows.task_manual_action_required', 'Task - Manual action required')}</option>
                    <option value="notification">{t('workflows.notification_send_alertemail', 'Notification - Send alert/email')}</option>
                    <option value="communication">{t('workflows.communication_send_smsemailvoice_outreach', 'Communication - Send SMS/Email/Voice outreach')}</option>
                    <option value="wait_for_response">{t('workflows.wait_for_response_wait_for', 'Wait for Response - Wait for member to respond')}</option>
                    <option value="approval">{t('workflows.approval_requires_approval', 'Approval - Requires approval')}</option>
                    <option value="condition">{t('workflows.condition_branch_based_on_criteria', 'Condition - Branch based on criteria')}</option>
                    <option value="api_call">{t('workflows.api_call_external_system_integration', 'API Call - External system integration')}</option>
                    <option value="wait">{t('workflows.wait_delay_before_next_step', 'Wait - Delay before next step')}</option>
                    <option value="form">{t('workflows.form_assign_form_from_library', 'Form - Assign form from library')}</option>
                    <option value="billing">{t('workflows.billing_capture_billing_information', 'Billing - Capture billing information')}</option>
                    <option value="pricing_agent">{t('workflows.pricing_agent_ai_negotiation', 'Pricing Agent - AI-powered pricing negotiation')}</option>
                  </select>
                )}
              </div>

              <div className="form-group">
                <StepPlacementFields
                  value={{
                    page_key: formData.page_key,
                    location_hint: formData.location_hint,
                    is_user_facing: formData.is_user_facing,
                    step_group: formData.step_group,
                  }}
                  onChange={(placement) => setFormData({ ...formData, ...placement })}
                  existingGroups={existingGroups}
                />
              </div>

              {/* Enhanced Mode: Use StepConfigurationPanel */}
              {viewMode === 'enhanced' && (
                <div className="form-group">
                  <label>{t('workflows.step_configuration', 'Step Configuration')}</label>
                  <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#fafafa'
                  }}>
                    <StepConfigurationPanel
                      stepTypeValue={selectedStepTypeValue}
                      baseType={formData.step_type as StepType}
                      config={formData.step_config || {}}
                      onChange={handleConfigChange}
                    />
                  </div>
                </div>
              )}

              {/* Classic Mode: Communication Configuration */}
              {viewMode === 'classic' && formData.step_type === 'communication' && (
                <div className="form-group" style={{ backgroundColor: '#f0f9ff', padding: '15px', borderRadius: '8px' }}>
                  <label><strong>📧 Communication Settings</strong></label>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.channel', 'Channel')}</label>
                    <select
                      value={formData.step_config?.channel || 'sms'}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, channel: e.target.value }
                      })}
                    >
                      <option value="sms">SMS</option>
                      <option value="email">{t('workflows.email', 'Email')}</option>
                      <option value="voice">{t('workflows.voice_call_ai', 'Voice Call (AI)')}</option>
                      <option value="phone">{t('workflows.phone_call_manual', 'Phone Call (Manual)')}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.expects_response || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, expects_response: e.target.checked }
                        })}
                      />{' '}
                      Wait for member response
                    </label>
                  </div>
                  {formData.step_config?.expects_response && (
                    <div style={{ marginTop: '10px' }}>
                      <label>Response Timeout (hours)</label>
                      <input
                        type="number"
                        min="1"
                        value={formData.step_config?.timeout_hours || 24}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, timeout_hours: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Classic Mode: Wait for Response Configuration */}
              {viewMode === 'classic' && formData.step_type === 'wait_for_response' && (
                <div className="form-group" style={{ backgroundColor: '#fef3c7', padding: '15px', borderRadius: '8px' }}>
                  <label><strong>⏳ Response Condition Settings</strong></label>
                  <div style={{ marginTop: '10px' }}>
                    <label>Required Channels (select all that apply)</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '5px' }}>
                      {['SMS', 'EMAIL', 'VOICE', 'PHONE'].map((channel) => (
                        <label key={channel}>
                          <input
                            type="checkbox"
                            checked={(formData.step_config?.required_channels || []).includes(channel)}
                            onChange={(e) => {
                              const current = formData.step_config?.required_channels || [];
                              const updated = e.target.checked
                                ? [...current, channel]
                                : current.filter((c: string) => c !== channel);
                              setFormData({
                                ...formData,
                                step_config: { ...formData.step_config, required_channels: updated }
                              });
                            }}
                          />{' '}
                          {channel}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.timeout_hours', 'Timeout (hours)')}</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.step_config?.timeout_hours || 24}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, timeout_hours: parseInt(e.target.value) }
                      })}
                    />
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.timeout_action', 'Timeout Action')}</label>
                    <select
                      value={formData.step_config?.timeout_action || 'escalate'}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, timeout_action: e.target.value }
                      })}
                    >
                      <option value="escalate">{t('workflows.escalate_to_supervisor', 'Escalate to supervisor')}</option>
                      <option value="skip">{t('workflows.skip_to_next_step', 'Skip to next step')}</option>
                      <option value="retry">{t('workflows.retry_communication', 'Retry communication')}</option>
                      <option value="notify">{t('workflows.notify_case_manager', 'Notify case manager')}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>Response Patterns (JSON)</label>
                    <textarea
                      placeholder='{"accept": ["yes", "1"], "reject": ["no", "2"]}'
                      value={formData.step_config?.response_patterns_json || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, response_patterns_json: e.target.value }
                      })}
                      rows={3}
                      style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
                    />
                    <small style={{ color: '#666' }}>
                      Define keywords that map to actions (accept, reject, reschedule)
                    </small>
                  </div>
                </div>
              )}

              {/* Classic Mode: Billing Configuration */}
              {viewMode === 'classic' && formData.step_type === 'billing' && (
                <div className="form-group" style={{ backgroundColor: '#ecfdf5', padding: '15px', borderRadius: '8px' }}>
                  <label><strong>💰 Billing Settings</strong></label>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.billing_type', 'Billing Type')}</label>
                    <select
                      value={formData.step_config?.billing_type || 'service'}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, billing_type: e.target.value }
                      })}
                    >
                      <option value="service">{t('workflows.service_billable_service_performed', 'Service - Billable service performed')}</option>
                      <option value="enrollment">{t('workflows.enrollment_initial_assessmentintake', 'Enrollment - Initial assessment/intake')}</option>
                      <option value="assessment">{t('workflows.assessment_comprehensive_assessment', 'Assessment - Comprehensive assessment')}</option>
                      <option value="care_plan">{t('workflows.care_plan_care_plan_development', 'Care Plan - Care plan development')}</option>
                      <option value="outreach">{t('workflows.outreach_member_outreach_activity', 'Outreach - Member outreach activity')}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.auto_populate || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, auto_populate: e.target.checked }
                        })}
                      />{' '}
                      Auto-populate from program billing activities
                    </label>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.save_as_draft || true}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, save_as_draft: e.target.checked }
                        })}
                      />{' '}
                      Save billing entry as draft (requires review before submission)
                    </label>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.default_place_of_service', 'Default Place of Service')}</label>
                    <select
                      value={formData.step_config?.place_of_service || '02'}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, place_of_service: e.target.value }
                      })}
                    >
                      <option value="02">02 - Telehealth</option>
                      <option value="11">11 - Office</option>
                      <option value="12">12 - Home</option>
                      <option value="99">99 - Other</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Classic Mode: Form Step Configuration */}
              {viewMode === 'classic' && formData.step_type === 'form' && (
                <div className="form-group" style={{ backgroundColor: '#eef2ff', padding: '15px', borderRadius: '8px' }}>
                  <label><strong>📝 Form Settings</strong></label>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.form_type', 'Form Type')}</label>
                    <select
                      value={formData.step_config?.form_type || 'custom'}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, form_type: e.target.value }
                      })}
                    >
                      <option value="intake_assessment">{t('workflows.intake_assessment', 'Intake Assessment')}</option>
                      <option value="care_plan">{t('workflows.care_plan', 'Care Plan')}</option>
                      <option value="consent">{t('workflows.consent_forms', 'Consent Forms')}</option>
                      <option value="progress_note">{t('workflows.progress_note', 'Progress Note')}</option>
                      <option value="eligibility">{t('workflows.eligibility_verification', 'Eligibility Verification')}</option>
                      <option value="custom">{t('workflows.custom_form', 'Custom Form')}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.navigation_url', 'Navigation URL')}</label>
                    <input
                      type="text"
                      placeholder="/members/{member_id}/encounters"
                      value={formData.step_config?.navigation_url || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, navigation_url: e.target.value }
                      })}
                    />
                    <small style={{ color: '#666' }}>
                      Use {'{member_id}'} as placeholder for dynamic member ID
                    </small>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.requires_signature || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, requires_signature: e.target.checked }
                        })}
                      />{' '}
                      {t('workflows.requires_electronic_signature', 'Requires electronic signature')}
                    </label>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.blocks_workflow !== false}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, blocks_workflow: e.target.checked }
                        })}
                      />{' '}
                      {t('workflows.blocks_workflow_progress', 'Form must be completed before workflow can proceed')}
                    </label>
                  </div>
                </div>
              )}

              {/* Classic Mode: Assessment Step Configuration */}
              {viewMode === 'classic' && (formData.step_type === 'assessment' || formData.step_type === 'form') && (
                <div className="form-group" style={{ backgroundColor: '#fef3c7', padding: '15px', borderRadius: '8px' }}>
                  <label><strong>📋 {formData.step_type === 'assessment' ? 'Assessment' : 'Form'} Settings</strong></label>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.select_form_template', 'Select Form / Assessment')}</label>
                    {formTemplates.length > 0 ? (
                      <select
                        value={formData.step_config?.form_template_id || ''}
                        onChange={(e) => {
                          const selectedId = e.target.value ? parseInt(e.target.value) : null;
                          const selectedForm = formTemplates.find(f => f.id === selectedId);
                          setFormData({
                            ...formData,
                            step_config: {
                              ...formData.step_config,
                              form_template_id: selectedId,
                              form_type: selectedForm?.form_type || 'custom',
                            },
                          });
                        }}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                      >
                        <option value="">-- Select a form template --</option>
                        {formTemplates.map((ft) => (
                          <option key={ft.id} value={ft.id}>
                            {ft.name} ({ft.form_type})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ marginTop: '5px', padding: '10px', backgroundColor: '#fef3c7', borderRadius: '6px', fontSize: '0.85rem', color: '#92400e' }}>
                        No form templates found. Create assessment forms in the <strong>Form Library</strong> first.
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.auto_score || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, auto_score: e.target.checked }
                        })}
                      />{' '}
                      {t('workflows.auto_calculate_score', 'Auto-calculate score')}
                    </label>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.blocks_workflow !== false}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, blocks_workflow: e.target.checked }
                        })}
                      />{' '}
                      {t('workflows.blocks_progress', 'Blocks workflow progress until completed')}
                    </label>
                  </div>
                </div>
              )}

              {/* Classic Mode: Document Step Configuration */}
              {viewMode === 'classic' && formData.step_type === 'document' && (
                <div className="form-group" style={{ backgroundColor: '#fce7f3', padding: '15px', borderRadius: '8px' }}>
                  <label><strong>📄 Document Settings</strong></label>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.document_types_required', 'Document Types Required')}</label>
                    {configuredDocTypes.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '5px' }}>
                        {configuredDocTypes.map((docType) => (
                          <label key={docType.name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input
                              type="checkbox"
                              checked={(formData.step_config?.required_documents || []).includes(docType.name)}
                              onChange={(e) => {
                                const current = formData.step_config?.required_documents || [];
                                const updated = e.target.checked
                                  ? [...current, docType.name]
                                  : current.filter((d: string) => d !== docType.name);
                                setFormData({
                                  ...formData,
                                  documentation_required: updated.length > 0,
                                  step_config: { ...formData.step_config, required_documents: updated }
                                });
                              }}
                            />
                            <span>{docType.name}</span>
                            <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>({docType.category_name})</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div style={{ marginTop: '5px', padding: '10px', backgroundColor: '#fef3c7', borderRadius: '6px', fontSize: '0.85rem', color: '#92400e' }}>
                        No document types configured. Go to <strong>Administration → Document Management</strong> to create document categories and types first.
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.requires_e_signature || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, requires_e_signature: e.target.checked }
                        })}
                      />{' '}
                      {t('workflows.requires_electronic_signature', 'Requires electronic signature')}
                    </label>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.navigation_url', 'Navigation URL')}</label>
                    <input
                      type="text"
                      placeholder="/members/{member_id}/documents"
                      value={formData.step_config?.navigation_url || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, navigation_url: e.target.value }
                      })}
                    />
                  </div>
                </div>
              )}

              {/* Classic Mode: Approval Step Configuration */}
              {viewMode === 'classic' && formData.step_type === 'approval' && (
                <div className="form-group" style={{ backgroundColor: '#dcfce7', padding: '15px', borderRadius: '8px' }}>
                  <label><strong>✅ Approval Settings</strong></label>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.approver_role', 'Approver Role')}</label>
                    <select
                      value={formData.step_config?.approver_role || 'supervisor'}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, approver_role: e.target.value }
                      })}
                    >
                      <option value="supervisor">{t('workflows.supervisor', 'Supervisor')}</option>
                      <option value="pcp">{t('workflows.primary_care_provider', 'Primary Care Provider (PCP)')}</option>
                      <option value="care_manager">{t('workflows.care_manager', 'Care Manager')}</option>
                      <option value="medical_director">{t('workflows.medical_director', 'Medical Director')}</option>
                      <option value="billing_manager">{t('workflows.billing_manager', 'Billing Manager')}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.requires_signature || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, requires_signature: e.target.checked }
                        })}
                      />{' '}
                      {t('workflows.requires_signature', 'Requires signature')}
                    </label>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.navigation_url', 'Navigation URL')}</label>
                    <input
                      type="text"
                      placeholder="/members/{member_id}/care-plan"
                      value={formData.step_config?.navigation_url || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, navigation_url: e.target.value }
                      })}
                    />
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.approval_timeout_hours', 'Approval Timeout (hours)')}</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.step_config?.timeout_hours || 72}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, timeout_hours: parseInt(e.target.value) }
                      })}
                    />
                  </div>
                </div>
              )}

              {/* Classic Mode: Task Step Configuration */}
              {viewMode === 'classic' && formData.step_type === 'task' && (
                <div className="form-group" style={{ backgroundColor: '#dbeafe', padding: '15px', borderRadius: '8px' }}>
                  <label><strong>📋 Task Settings</strong></label>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.task_description', 'Task Description')}</label>
                    <textarea
                      placeholder="Describe the action the user needs to take..."
                      value={formData.step_config?.description || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, description: e.target.value }
                      })}
                      rows={2}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.navigation_url', 'Navigation URL (optional)')}</label>
                    <input
                      type="text"
                      placeholder="/telehealth or /members/{member_id}/profile"
                      value={formData.step_config?.navigation_url || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, navigation_url: e.target.value }
                      })}
                    />
                    <small style={{ color: '#666' }}>
                      Page to navigate when this task is started
                    </small>
                  </div>
                </div>
              )}

              {/* Classic Mode: Pricing Agent Configuration */}
              {viewMode === 'classic' && formData.step_type === 'pricing_agent' && (
                <div className="form-group" style={{ backgroundColor: '#f5f3ff', padding: '15px', borderRadius: '8px' }}>
                  <label><strong>🤖 AI Pricing Agent Settings</strong></label>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.negotiation_channel', 'Negotiation Channel')}</label>
                    <select
                      value={formData.step_config?.channel || 'email'}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, channel: e.target.value }
                      })}
                    >
                      <option value="email">{t('workflows.email', 'Email')}</option>
                      <option value="sms">SMS</option>
                      <option value="voice">{t('workflows.voice_ai', 'Voice AI')}</option>
                      <option value="portal">{t('workflows.provider_portal', 'Provider Portal')}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.negotiation_strategy', 'Negotiation Strategy')}</label>
                    <select
                      value={formData.step_config?.strategy || 'balanced'}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, strategy: e.target.value }
                      })}
                    >
                      <option value="balanced">{t('workflows.balanced_fair_market_rate', 'Balanced - Fair market rate')}</option>
                      <option value="aggressive">{t('workflows.aggressive_lower_offers', 'Aggressive - Lower offers')}</option>
                      <option value="accommodating">{t('workflows.accommodating_quick_agreement', 'Accommodating - Quick agreement')}</option>
                      <option value="urgent">{t('workflows.urgent_fast_resolution', 'Urgent - Fast resolution')}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.max_negotiation_rounds', 'Max Negotiation Rounds')}</label>
                    <select
                      value={formData.step_config?.max_rounds || 5}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, max_rounds: parseInt(e.target.value) }
                      })}
                    >
                      <option value={3}>3 rounds</option>
                      <option value={5}>5 rounds (recommended)</option>
                      <option value={7}>7 rounds</option>
                      <option value={10}>10 rounds</option>
                    </select>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.auto_initiate !== false}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, auto_initiate: e.target.checked }
                        })}
                      />{' '}
                      Auto-initiate when provider is selected
                    </label>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.step_config?.escalate_on_timeout || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          step_config: { ...formData.step_config, escalate_on_timeout: e.target.checked }
                        })}
                      />{' '}
                      Escalate to human if no agreement reached
                    </label>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label>{t('workflows.response_timeout_hours', 'Response Timeout (hours)')}</label>
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={formData.step_config?.timeout_hours || 48}
                      onChange={(e) => setFormData({
                        ...formData,
                        step_config: { ...formData.step_config, timeout_hours: parseInt(e.target.value) }
                      })}
                    />
                    <small style={{ color: '#666' }}>Time to wait for provider response between rounds</small>
                  </div>
                </div>
              )}

              {/* Classic Mode: Manual Config Key-Value Entry */}
              {viewMode === 'classic' && (
                <div className="form-group">
                  <label>{t('workflows.step_configuration', 'Additional Configuration')}</label>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '4px', padding: '10px' }}>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      <input
                        type="text"
                        placeholder="Config key (e.g., email_template)"
                        value={configKey}
                        onChange={(e) => setConfigKey(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <input
                        type="text"
                        placeholder={t('workflows.config_value', 'Config value')}
                        value={configValue}
                        onChange={(e) => setConfigValue(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <button type="button" className="btn btn-sm btn-secondary" onClick={handleAddConfig}>
                        {t('workflows.add', 'Add')}
                      </button>
                    </div>
                    {Object.keys(formData.step_config || {}).length > 0 && (
                      <div>
                        <strong>{t('workflows.current_configuration', 'Current Configuration:')}</strong>
                        <ul style={{ listStyle: 'none', padding: 0, marginTop: '5px' }}>
                          {Object.entries(formData.step_config || {}).map(([key, value]) => (
                            <li
                              key={key}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '5px',
                                backgroundColor: '#f9fafb',
                                marginBottom: '5px',
                                borderRadius: '4px',
                              }}
                            >
                              <span>
                                <code>{key}</code>: {String(value)}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => handleRemoveConfig(key)}
                              >
                                {t('workflows.remove', 'Remove')}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.required}
                    onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                  />{' '}
                  Required step (workflow cannot proceed without completion)
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  {t('workflows.cancel', 'Cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save className="w-4 h-4" style={{ display: 'inline', marginRight: '5px' }} />
                  {editingStep ? 'Update Step' : 'Add Step'}
                </button>
              </div>
          </form>
        </div>
      )}

      {/* Visual Workflow Display — stood down while the step editor has the page */}
      {showAddForm ? null : loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('workflows.loading_workflow_steps', 'Loading workflow steps...')}</p>
        </div>
      ) : (
        /* One canvas. Orientation was never a question with three answers: a
           process reads left to right, and the three modes made the toolbar
           busier without making anything clearer. */
        <div style={{ padding: '20px' }}>
          <WorkflowCanvas
            steps={steps}
            templateId={templateId}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSaved={refetch}
            onOpenBackground={(step) => {
              setBackgroundForStep(step.id);
              setPane('notes');
            }}
            loading={loading}
            onAddStep={(seed) => {
              // A shape placed from the palette opens the form already carrying
              // where it was dropped and what it is, so the box appears where
              // the click landed rather than at the end of the row.
              setFormData({
                ...formData,
                step_order: steps.length + 1,
                step_type: 'task',
                step_name: '',
                step_config: {},
                page_key: null,
                location_hint: null,
                is_user_facing: true,
                step_group: null,
                position_x: seed?.position_x ?? null,
                position_y: seed?.position_y ?? null,
                canvas_shape: seed?.canvas_shape ?? null,
              });
              if (seed?.stepTypeValue) setSelectedStepTypeValue(seed.stepTypeValue);
              setShowAddForm(true);
            }}
          />
        </div>
      )}

      {/* AI Suggestions Panel */}
      {showAISuggestions && (
        <div style={{ marginTop: '24px' }}>
          <WorkflowAISuggestions
            templateId={templateId}
            templateName={templateName}
            currentSteps={steps}
            onAddSuggestedStep={handleAddSuggestedStep}
            onViewSimilarWorkflow={(workflowId) => {
              console.log('View similar workflow:', workflowId);
              // Could navigate to the similar workflow
            }}
          />
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default WorkflowStepBuilder;
